import { useAuth } from '@/app/_auth/auth-hooks'
import { PromptFormValues } from '@/app/_common/prompt-form'
import { CREATE_WORKFLOW_429_ERROR } from '@/app/chat/_common/use-chat-data-query'
import { taskManagerApiPaths } from '@/hooks/api/_paths'
import { allWorkflowsKeyFactory } from '@/hooks/api/use-all-workflows-query'
import { useApiRequest } from '@/lib/http-utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'

type CreateWorkflowAPIRequest = {
  content: string
  name: string
  role: 'user'
  metadata: { [key: string]: any }
  response_format: { [key: string]: any }
}

type CreateWorkflowAPIResponse = {
  workflowId: string
  title: string
}

function useCreateWorkflowMutation() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { apiRequest, domainId } = useApiRequest()
  return useMutation({
    mutationKey: ['create-workflow'],
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: allWorkflowsKeyFactory({ domainId }) })
    },
    mutationFn: async ({ prompt, files }: PromptFormValues): Promise<CreateWorkflowAPIResponse> => {
      const requestData = port(prompt, user?.fullName ?? '')
      const formData = new FormData()
      formData.append('data', JSON.stringify(requestData))
      if (files && files.length > 0) {
        for (const file of files) {
          formData.append('files', file)
        }
      }
      const response = await apiRequest({
        path: taskManagerApiPaths.workflows({ domainId }),
        type: 'MULTIPART',
        options: {
          method: 'POST',
          body: formData,
        },
      })

      if (!response.ok) {
        throw new Error(
          response.status === 429 ? CREATE_WORKFLOW_429_ERROR : 'FAILED_TO_CREATE_WORKFLOW',
        )
      }

      const data = (await response.json()) as CreateWorkflowAPIResponse

      return data
    },
  })
}

const port = (content: string, name: string): CreateWorkflowAPIRequest => ({
  content,
  name,
  role: 'user',
  metadata: {},
  response_format: {},
})

export { useCreateWorkflowMutation }
